import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Save, RotateCcw, Eye, MessageSquare, ChevronDown, ChevronUp, Search, Send, RefreshCw, Plus, X } from 'lucide-react';
import { useAuth } from '@/hooks/useApi';
import { useTenantContext } from '@/contexts/TenantContext';
import { apiClient } from '@/lib/api';

interface SmsTemplate {
  template_type: string;
  message: string;
  enabled: boolean;
}

interface TemplateGroup {
  name: string;
  color: string;
  templates: Record<string, SmsTemplate>;
}

const TEMPLATE_GROUPS: TemplateGroup[] = [
  {
    name: 'Queue Operations',
    color: 'bg-blue-50',
    templates: {
      'queue_joined': {
        template_type: 'queue_joined',
        message: 'Welcome to {location_label}! You are #{position} in line. We will text you when it\'s your turn. Reply LEAVE to exit the queue.',
        enabled: true
      },
      'now_serving': {
        template_type: 'now_serving',
        message: 'It\'s your turn! Please come to {location_label} for service. You have 5 minutes to arrive.',
        enabled: true
      },
      'position_update': {
        template_type: 'position_update',
        message: 'Queue update: You are now #{position} in line for {location_label}.',
        enabled: true
      },
      'queue_closed': {
        template_type: 'queue_closed',
        message: 'The queue for {location_label} is now closed. Thank you for your patience.',
        enabled: true
      }
    }
  },
  {
    name: 'No-Show System',
    color: 'bg-red-50',
    templates: {
      'no_show_warning': {
        template_type: 'no_show_warning',
        message: 'You missed your turn at {location_label}. You have been moved to the back of the line{reason_suffix}. Reply LEAVE to exit.',
        enabled: true
      },
      'no_show_delayed': {
        template_type: 'no_show_delayed',
        message: 'You missed your turn and must wait {delay_minutes} minutes before rejoining {location_label}. Strike {strike_count}/{max_strikes}.',
        enabled: true
      },
      'max_strikes_reached': {
        template_type: 'max_strikes_reached',
        message: 'You have reached the maximum strikes ({max_strikes}) for {location_label} and cannot rejoin today.',
        enabled: true
      }
    }
  },
  {
    name: 'Status Messages',
    color: 'bg-green-50',
    templates: {
      'status_response': {
        template_type: 'status_response',
        message: 'You are #{position} in line for {location_label}.',
        enabled: true
      },
      'left_queue': {
        template_type: 'left_queue',
        message: 'You have left the queue for {location_label}. Thanks for visiting!',
        enabled: true
      },
      'pushed_back': {
        template_type: 'pushed_back',
        message: 'You have been moved back in the queue for {location_label}{reason_suffix}. You are now #{position} in line.',
        enabled: true
      },
      'close_notice': {
        template_type: 'close_notice',
        message: 'Your visit is complete. Thanks!',
        enabled: true
      },
      'cancel_notice': {
        template_type: 'cancel_notice',
        message: 'You have been removed from the queue. Thanks!',
        enabled: true
      }
    }
  },
  {
    name: 'User Commands',
    color: 'bg-orange-50',
    templates: {
      'help_response': {
        template_type: 'help_response',
        message: 'Commands: STATUS (your position), LEAVE (exit queue). You are #{position} for {location_label}.',
        enabled: true
      },
      'invalid_command': {
        template_type: 'invalid_command',
        message: 'Invalid command. Reply STATUS for your position or LEAVE to exit the queue for {location_label}.',
        enabled: true
      },
      'already_in_queue': {
        template_type: 'already_in_queue',
        message: 'You are already in line for {location_label} at position #{position}.',
        enabled: true
      }
    }
  },
  {
    name: 'Party Size Changes',
    color: 'bg-teal-50',
    templates: {
      'change_party_size_ok': {
        template_type: 'change_party_size_ok',
        message: 'Your party size has been updated to {party_size} for {location_label}. We\'ll keep your place in line.',
        enabled: true
      },
      'change_party_size_too_large': {
        template_type: 'change_party_size_too_large',
        message: 'Sorry, we can\'t increase your party size to {requested_size} for {location_label}. Maximum allowed is {max_party_size}. Please see a staff member for help.',
        enabled: true
      },
      'change_party_size_too_small': {
        template_type: 'change_party_size_too_small',
        message: 'Party size must be at least 1 for {location_label}. If you need to leave the queue, reply LEAVE.',
        enabled: true
      },
      'change_party_size_not_allowed': {
        template_type: 'change_party_size_not_allowed',
        message: 'This queue only allows {allowed_direction} changes to party size. Please see a staff member if you need help.',
        enabled: true
      },
      'change_party_size_not_in_queue': {
        template_type: 'change_party_size_not_in_queue',
        message: 'We couldn\'t find an active queue entry to change your party size. Reply STATUS to check your position.',
        enabled: true
      },
      'change_party_size_removed': {
        template_type: 'change_party_size_removed',
        message: 'Your party size was set to 0, so we\'ve removed you from the queue for {location_label}. Thanks for letting us know!',
        enabled: true
      }
    }
  },
  {
    name: 'System Messages',
    color: 'bg-purple-50',
    templates: {
      'queue_full': {
        template_type: 'queue_full',
        message: 'Sorry, the queue for {location_label} is currently full. Please try again later.',
        enabled: true
      },
      'queue_not_available': {
        template_type: 'queue_not_available',
        message: 'The queue for {location_label} is not currently available.',
        enabled: true
      },
      'system_error': {
        template_type: 'system_error',
        message: 'We\'re experiencing technical difficulties. Please try again in a few minutes.',
        enabled: true
      }
    }
  }
];

// Flatten all templates for easy access
const DEFAULT_TEMPLATES: Record<string, SmsTemplate> = TEMPLATE_GROUPS.reduce((acc, group) => {
  return { ...acc, ...group.templates };
}, {});

const TEMPLATE_DESCRIPTIONS: Record<string, string> = {
  // Queue Operations
  'queue_joined': 'Sent when a customer joins a queue',
  'now_serving': 'Sent when it\'s the customer\'s turn to be served',
  'position_update': 'Sent when customer position changes significantly',
  'queue_closed': 'Sent when a queue is closed with waiting customers',
  
  // No-Show System
  'no_show_warning': 'Sent when customer misses their turn',
  'no_show_delayed': 'Sent when customer must wait before rejoining after no-show',
  'max_strikes_reached': 'Sent when customer reaches maximum strikes',
  
  // Status Messages
  'status_response': 'Response to STATUS command',
  'left_queue': 'Sent when customer leaves the queue',
  'pushed_back': 'Sent when customer is moved back in queue',
  'close_notice': 'Sent when a customer\'s service is completed',
  'cancel_notice': 'Sent when a customer is removed from the queue by staff',

  // Party Size Changes
  'change_party_size_ok': 'Sent when party size is successfully changed',
  'change_party_size_too_large': 'Sent when requested party size exceeds maximum',
  'change_party_size_too_small': 'Sent when party size is set below minimum (1)',
  'change_party_size_not_allowed': 'Sent when party size change direction is not allowed',
  'change_party_size_not_in_queue': 'Sent when no active queue entry found for party size change',
  'change_party_size_removed': 'Sent when party size set to 0 (graceful removal)',
  
  // User Commands
  'help_response': 'Response to HELP command',
  'invalid_command': 'Response to unrecognized SMS commands',
  'already_in_queue': 'Sent when customer tries to join queue they\'re already in',
  
  // System Messages
  'queue_full': 'Sent when queue has reached capacity',
  'queue_not_available': 'Sent when queue is closed or unavailable',
  'system_error': 'Sent when system encounters an error'
};

const VARIABLE_DEFINITIONS: Record<string, string> = {
  '{tenant_id}': 'The unique tenant identifier (e.g., SALTCON)',
  '{location_id}': 'The location/game identifier (e.g., Bomb_Busters)',
  '{service_id}': 'The service identifier (e.g., svc-standard)',
  '{position}': 'Customer\'s current position in queue (e.g., 3)',
  '{expected_wait}': 'Estimated wait time for customer (e.g., "5-10 minutes")',
  '{reason_suffix}': 'Additional reason text when pushing back (e.g., \' due to no-show\')',
  '{strike_count}': 'Customer\'s current strike count (0-max_strikes)',
  '{max_strikes}': 'Maximum strikes allowed before max strikes status',
  '{delay_minutes}': 'Minutes customer must wait after no-show before rejoining',
  '{location}': 'Short alias for location_id',
  '{service}': 'Short alias for service_id',
  '{tenant}': 'Short alias for tenant_id',
  '{location_label}': 'Human-readable location label (e.g., \'Games\' instead of \'Bomb_Busters\')',
  '{service_label}': 'Human-readable service name (e.g., \'Foundations of Rome\' instead of \'foundations_of_rome\')',
  '{party_size}': 'Current party size (e.g., 4)',
  '{requested_size}': 'Requested party size that was rejected',
  '{max_party_size}': 'Maximum party size allowed for the queue',
  '{allowed_direction}': 'Allowed change direction (e.g., "increase only", "decrease only")',
  '{phone_number}': 'Phone number the message is being sent to (e.g., +18015551234)',
  '{service_start_time}': 'Service start time in ISO 8601 format with Z suffix (e.g., 2024-01-15T09:30:00Z)'
};

const AVAILABLE_VARIABLES = Object.keys(VARIABLE_DEFINITIONS);

export function SmsTemplateEditor() {
  const { user, isSuperAdmin } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const [templates, setTemplates] = useState<Record<string, SmsTemplate>>(DEFAULT_TEMPLATES);
  const [activeTemplate, setActiveTemplate] = useState('queue_joined');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [contextMenu, setContextMenu] = useState<{x: number, y: number, show: boolean}>({x: 0, y: 0, show: false});
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [variablesPanelOpen, setVariablesPanelOpen] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const activeTenantId = isSuperAdmin ? selectedTenantId : user?.tenant_from_group;

  useEffect(() => {
    loadTemplates();
  }, [activeTenantId]);
  
  useEffect(() => {
    setMessage('');
    setHasUnsavedChanges(false);
  }, [activeTemplate]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'Escape') {
        setContextMenu({x: 0, y: 0, show: false});
        setVariablesPanelOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTemplate, templates]);

  const loadTemplates = async () => {
    if (!activeTenantId) {
      setTemplates(DEFAULT_TEMPLATES);
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiClient.getSmsTemplates(activeTenantId);
      
      const mergedTemplates = { ...DEFAULT_TEMPLATES };
      if (response.templates) {
        Object.keys(response.templates).forEach(key => {
          if (DEFAULT_TEMPLATES[key]) {
            mergedTemplates[key] = response.templates[key];
          }
        });
      }
      
      setTemplates(mergedTemplates);
    } catch (error) {
      console.error('Failed to load SMS templates:', error);
      setTemplates(DEFAULT_TEMPLATES);
      setMessage('Loaded default templates due to API error.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!activeTenantId) {
      setMessage('No tenant selected');
      return;
    }

    setSaving(true);
    try {
      await apiClient.saveSmsTemplate(activeTenantId, activeTemplate, {
        template_type: activeTemplate,
        message: templates[activeTemplate].message,
        enabled: templates[activeTemplate].enabled
      });
      setMessage('Template saved successfully!');
      setHasUnsavedChanges(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save template:', error);
      setMessage('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaultTemplate = DEFAULT_TEMPLATES[activeTemplate];
    if (defaultTemplate) {
      setTemplates(prev => ({
        ...prev,
        [activeTemplate]: { ...defaultTemplate }
      }));
      setHasUnsavedChanges(true);
      setMessage('Template reset to default');
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const handleMessageChange = (newMessage: string) => {
    setTemplates(prev => ({
      ...prev,
      [activeTemplate]: {
        ...prev[activeTemplate],
        message: newMessage
      }
    }));
    setHasUnsavedChanges(true);
  };

  const insertVariable = (variable: string) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentMessage = templates[activeTemplate].message;
      const newMessage = currentMessage.substring(0, start) + variable + currentMessage.substring(end);
      
      handleMessageChange(newMessage);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const handleSendTest = async () => {
    if (!testPhoneNumber || !activeTenantId) return;
    
    setSendingTest(true);
    try {
      setMessage(`Test SMS would be sent to ${testPhoneNumber} (feature not yet implemented)`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to send test SMS');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const filteredGroups = TEMPLATE_GROUPS.map(group => ({
    ...group,
    templates: Object.fromEntries(
      Object.entries(group.templates).filter(([key, template]) => {
        const matchesSearch = searchTerm === '' || 
          key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          TEMPLATE_DESCRIPTIONS[key]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          template.message.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      })
    )
  })).filter(group => Object.keys(group.templates).length > 0);

  const renderPreview = () => {
    let preview = templates[activeTemplate]?.message || '';

    const sampleData: Record<string, string> = {
      '{tenant_id}': 'SALTCON',
      '{location_id}': 'Bomb_Busters',
      '{service_id}': 'svc-standard',
      '{position}': '3',
      '{expected_wait}': '5-10 minutes',
      '{reason_suffix}': ' due to no-show',
      '{strike_count}': '1',
      '{max_strikes}': '3',
      '{delay_minutes}': '15',
      '{location}': 'Bomb_Busters',
      '{service}': 'svc-standard',
      '{tenant}': 'SALTCON',
      '{location_label}': 'Bomb Busters',
      '{service_label}': 'Standard Service',
      '{party_size}': '4',
      '{requested_size}': '8',
      '{max_party_size}': '6',
      '{allowed_direction}': 'increase only',
      '{phone_number}': '+18015551234',
      '{service_start_time}': '2024-01-15T09:30:00Z'
    };

    Object.entries(sampleData).forEach(([variable, value]) => {
      preview = preview.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return preview;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="h-96 bg-gray-200 rounded"></div>
            </div>
            <div className="lg:col-span-2">
              <div className="h-96 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {message && (
        <Alert className={message.includes('success') ? 'border-green-200 bg-green-50' : message.includes('error') || message.includes('Failed') ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template Selection Panel */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                SMS Templates
                <Badge variant="outline">{Object.keys(templates).length}</Badge>
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-3 max-h-96 overflow-y-auto">
              {filteredGroups.map((group) => {
                const isCollapsed = collapsedGroups[group.name];
                const templateCount = Object.keys(group.templates).length;
                
                return (
                  <div key={group.name} className={`${group.color} rounded-lg p-3`}>
                    <button
                      onClick={() => toggleGroup(group.name)}
                      className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      <span className="flex items-center gap-2">
                        {group.name}
                        <Badge variant="secondary" className="text-xs">{templateCount}</Badge>
                      </span>
                      {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    </button>
                    
                    {!isCollapsed && (
                      <div className="mt-2 space-y-1">
                        {Object.entries(group.templates).map(([key, template]) => (
                          <button
                            key={key}
                            onClick={() => setActiveTemplate(key)}
                            className={`w-full text-left p-2 rounded text-sm transition-colors ${
                              activeTemplate === key
                                ? 'bg-blue-600 text-white'
                                : 'hover:bg-white/50 text-gray-700'
                            }`}
                          >
                            <div className={`font-medium ${activeTemplate === key ? 'text-white' : 'text-gray-900'}`}>
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                            <div className={`text-xs ${activeTemplate === key ? 'text-white/80' : 'text-gray-500'} truncate`}>
                              {TEMPLATE_DESCRIPTIONS[key]}
                            </div>
                            {!template.enabled && (
                              <Badge variant="secondary" className="mt-1 text-xs">Disabled</Badge>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Template Editor Panel */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    {activeTemplate.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    {hasUnsavedChanges && <span className="text-orange-500">*</span>}
                  </CardTitle>
                  <CardDescription>{TEMPLATE_DESCRIPTIONS[activeTemplate]}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setVariablesPanelOpen(!variablesPanelOpen)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Variables
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                  <Button 
                    onClick={handleSave} 
                    disabled={saving || !activeTenantId}
                    size="sm"
                  >
                    {saving ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="message">Message Template</Label>
                <Textarea
                  ref={textareaRef}
                  id="message"
                  value={templates[activeTemplate]?.message || ''}
                  onChange={(e) => handleMessageChange(e.target.value)}
                  placeholder="Enter your SMS template message..."
                  className="min-h-32 font-mono text-sm"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      show: true
                    });
                  }}
                />
                <div className="text-xs text-gray-500">
                  {templates[activeTemplate]?.message?.length || 0} characters
                  {templates[activeTemplate]?.message?.length > 160 && (
                    <span className="text-orange-500 ml-2">
                      (May be split into multiple SMS)
                    </span>
                  )}
                </div>
              </div>

              {/* Test SMS Section */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">Test SMS</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Phone number (e.g., +1234567890)"
                    value={testPhoneNumber}
                    onChange={(e) => setTestPhoneNumber(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSendTest}
                    disabled={!testPhoneNumber || sendingTest}
                    variant="outline"
                    size="sm"
                  >
                    {sendingTest ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-1" />
                    )}
                    Send Test
                  </Button>
                </div>
              </div>

              {/* Preview Section */}
              <div className="border-t pt-4">
                <button
                  onClick={() => setPreviewCollapsed(!previewCollapsed)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview with Sample Data
                  {previewCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </button>
                
                {!previewCollapsed && (
                  <div className="bg-gray-50 p-3 rounded border text-sm">
                    <div className="font-medium text-gray-600 mb-1">SMS Preview:</div>
                    <div className="bg-white p-2 rounded border">{renderPreview()}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Variables Panel */}
      {variablesPanelOpen && (
        <Card className="mt-4">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Available Variables</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVariablesPanelOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {AVAILABLE_VARIABLES.map((variable) => (
                <button
                  key={variable}
                  onClick={() => insertVariable(variable)}
                  className="text-left p-3 border rounded hover:bg-gray-50 transition-colors"
                >
                  <div className="font-mono text-sm text-blue-600 font-medium">{variable}</div>
                  <div className="text-xs text-gray-500 mt-1">{VARIABLE_DEFINITIONS[variable]}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Context Menu */}
      {contextMenu.show && (
        <div
          className="fixed bg-white border rounded shadow-lg py-1 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onMouseLeave={() => setContextMenu({x: 0, y: 0, show: false})}
        >
          {AVAILABLE_VARIABLES.map((variable) => (
            <button
              key={variable}
              onClick={() => {
                insertVariable(variable);
                setContextMenu({x: 0, y: 0, show: false});
              }}
              className="block w-full text-left px-3 py-1 hover:bg-gray-100 text-sm"
            >
              <span className="font-mono text-blue-600">{variable}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}